import type { ReactNode } from "react";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useRouter } from "next/router";
import { identifyUser } from "@/util/analytics";
import {
  getGlobalBotDetails,
  getProfileDetailsApi,
  getUnseenNotificationsCountApi,
} from "@/services/axios/protectedApi";
import i18n, { isSupportedLanguageCode } from "@/src/i18n";
import type { RoleDisplayNames } from "@/util/roleDisplayNames";
import { getRoleDisplayName as resolveRoleDisplayName } from "@/util/roleDisplayNames";

interface Organization {
  _id: string;
  name: string;
  type?: string[];
  theme?: any;
  customFeatureNames?: Record<string, string>;
  roleDisplayNames?: RoleDisplayNames;
  customRole?: { name?: string; allowedTabs?: string[] } | null;
  coach: boolean;
  enableEvaluation: boolean;
  voiceProviders?: string[];
  [key: string]: any;
  chatInRoleplay: boolean;
}

interface User {
  userId: string;
  email: string;
  role: string;
  orgName: string;
  name?: string;
  profileImgUrl?: string;
  botIcon?: string;
  allowSelfAssignEvaluation?: boolean;
  profileLandmarks?: Record<string, any> | null;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  org: Organization | null;
  role: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  notificationCount: number;
  setNotificationCount: React.Dispatch<React.SetStateAction<number>>;
  fetchNotificationCount: () => Promise<void>;
  isBotChatEnabled: boolean;
  fetchBotDetails: (currentRole?: string) => Promise<void>;
  login: (data: any) => Promise<void>;
  logout: () => void;
  getFeatureName: (defaultName: string, featureKey: string) => string;
  getRoleDisplayName: (role: string, options?: { plural?: boolean }) => string;
  getDefaultLandingRoute: (role?: string, orgData?: any) => string;
  updateOrg: (org: Organization) => void;
  mergeOrg: (fields: Partial<Organization>) => void;
  refreshUser: () => Promise<any>;
  updateSessionFromStorage: () => void;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let logoutHandler: (() => void) | null = null;
let refreshUserInFlight: Promise<any> | null = null;

export const registerLogoutHandler = (handler: () => void) => {
  logoutHandler = handler;
};

export const logoutFromInterceptor = () => {
  logoutHandler?.();
};

export const AuthStoreProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [role, setRole] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [isBotChatEnabled, setIsBotChatEnabled] = useState<boolean>(false);
  const router = useRouter();

  const fetchNotificationCount = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const updateLastActivity = localStorage.getItem("orgName") !== null;
      const res = await getUnseenNotificationsCountApi(updateLastActivity);
      setNotificationCount(res?.data?.data || 0);
    } catch {
      setNotificationCount(0);
    }
  }, []);

  const fetchBotDetails = useCallback(async (currentRole?: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const effectiveRole =
        currentRole ||
        (typeof window !== "undefined" ? localStorage.getItem("role") : "") ||
        "user";
      const permissions = await getGlobalBotDetails();
      let check = false;
      if (effectiveRole === "user" || effectiveRole === "manager") {
        check =
          Boolean(permissions?.data?.permissions?.conditions) &&
          Boolean(permissions?.data?.permissions?.published);
      } else if (effectiveRole === "admin" || effectiveRole === "custom") {
        check = Boolean(permissions?.data?.permissions?.conditions);
      }
      setIsBotChatEnabled(check);
    } catch (error) {
      console.error("Error fetching bot permissions:", error);
      setIsBotChatEnabled(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (refreshUserInFlight) {
      return refreshUserInFlight;
    }

    refreshUserInFlight = (async () => {
      try {
        const updateLastActivity = localStorage.getItem("orgName") !== null;
        const res = await getProfileDetailsApi(updateLastActivity);
        if (res && res.data.code === 200) {
          const profileData = res?.data?.data;
          if (profileData) {
            const updatedUser = {
              userId: profileData._id || "",
              email: profileData.email || "",
              orgName: profileData?.org?.organizationName || "",
              orgId: profileData?.org?._id || "",
              role: profileData.role || "",
              name: profileData.name || "",
              profileImgUrl: profileData.profileImgUrl || "",
              botIcon: profileData?.globalBotData?.botIcon || "",
              allowSelfAssignEvaluation: profileData.allowSelfAssignEvaluation,
              ...profileData,
            };
            setUser(updatedUser);
            setRole(profileData.role || "");

            await i18n.changeLanguage(
              isSupportedLanguageCode(profileData.preferredLanguage)
                ? profileData.preferredLanguage
                : "en"
            );
            if (profileData.org) {
              setOrg(profileData.org);
              localStorage.setItem("org", JSON.stringify(profileData.org));
              localStorage.setItem(
                "orgName",
                profileData.org.organizationName || ""
              );
            }

            if (profileData.name)
              localStorage.setItem("name", profileData.name);
            if (profileData.profileImgUrl)
              localStorage.setItem("profileImgUrl", profileData.profileImgUrl);
            if (profileData?.globalBotData?.botIcon)
              localStorage.setItem(
                "botIcon",
                profileData.globalBotData.botIcon
              );
            if (profileData.allowSelfAssignEvaluation !== undefined)
              localStorage.setItem(
                "allowSelfAssignEvaluation",
                String(profileData.allowSelfAssignEvaluation)
              );
            if (profileData.profileLandmarks !== undefined) {
              localStorage.setItem(
                "profileLandmarks",
                JSON.stringify(profileData.profileLandmarks || {})
              );
            }
            if (profileData.openInAppButton !== undefined)
              localStorage.setItem(
                "openinapp",
                profileData.openInAppButton ? "true" : "false"
              );

            localStorage.setItem("email", profileData.email || "");
            localStorage.setItem("userId", profileData._id || "");
            localStorage.setItem("role", profileData.role || "");

            identifyUser(
              {
                _id: profileData._id,
                email: profileData.email,
                role: profileData.role,
              },
              profileData.org || null
            );

            return profileData;
          }
        }
        return null;
      } catch (error) {
        console.error("Error refreshing user context:", error);
        return null;
      } finally {
        refreshUserInFlight = null;
      }
    })();

    return refreshUserInFlight;
  }, []);

  const loadFromStorage = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      void fetchNotificationCount();

      const email = localStorage.getItem("email") || "";
      const orgName = localStorage.getItem("orgName") || "";
      const userId = localStorage.getItem("userId") || "";
      const storedRole = localStorage.getItem("role") || "";
      const name = localStorage.getItem("name") || "";
      const profileImgUrl = localStorage.getItem("profileImgUrl") || "";
      const botIcon = localStorage.getItem("botIcon") || "";
      const allowSelfAssignEvaluation =
        localStorage.getItem("allowSelfAssignEvaluation") === "true";
      const storedOrg = localStorage.getItem("org");
      const storedProfileLandmarks = localStorage.getItem("profileLandmarks");
      let profileLandmarks = null;
      let parsedOrg: any = null;
      if (storedProfileLandmarks) {
        try {
          profileLandmarks = JSON.parse(storedProfileLandmarks);
        } catch (e) {
          console.error("Error parsing profileLandmarks from localStorage:", e);
        }
      }
      if (storedOrg) {
        try {
          parsedOrg = JSON.parse(storedOrg);
        } catch (e) {
          console.error("Error parsing org from localStorage:", e);
        }
      }

      void fetchBotDetails(storedRole);

      setUser({
        userId,
        email,
        orgName,
        orgId: parsedOrg?._id || "",
        role: storedRole,
        name,
        profileImgUrl,
        botIcon,

        allowSelfAssignEvaluation,
        profileLandmarks,
      });

      setRole(storedRole);

      if (parsedOrg) {
        setOrg(parsedOrg);
        identifyUser({ _id: userId, email, role: storedRole }, parsedOrg);
      } else {
        identifyUser({ userId, email, role: storedRole });
      }

      // If token exists, fetch profile once on startup
      void refreshUser();

      if (!localStorage.getItem("nextActivityUpdateAt")) {
        localStorage.setItem(
          "nextActivityUpdateAt",
          String(Date.now() + 5 * 60 * 1000)
        );
      }
    } catch (error) {
      console.error("Error loading auth from session:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchNotificationCount, fetchBotDetails, refreshUser]);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  const login = async (data: any) => {
    const {
      role,
      token,
      email,
      orgName,
      userId,
      org,
      name,
      profileImgUrl,
      globalBotData,
      allowSelfAssignEvaluation,
      profileLandmarks,
    } = data;

    // Store keys in localStorage
    localStorage.setItem("token", token);
    localStorage.setItem("email", email);
    localStorage.setItem("orgName", orgName || "");
    localStorage.setItem("userId", userId || "");
    localStorage.setItem("role", role || "");
    localStorage.setItem("org", JSON.stringify(org || null));

    await i18n.changeLanguage(
      isSupportedLanguageCode(data.preferredLanguage)
        ? data.preferredLanguage
        : "en"
    );
    localStorage.setItem(
      "nextActivityUpdateAt",
      String(Date.now() + 5 * 60 * 1000)
    );
    if (name) localStorage.setItem("name", name);
    if (profileImgUrl) localStorage.setItem("profileImgUrl", profileImgUrl);
    if (globalBotData?.botIcon)
      localStorage.setItem("botIcon", globalBotData.botIcon);
    if (globalBotData?.name)
      localStorage.setItem("botName", globalBotData.name);
    if (allowSelfAssignEvaluation !== undefined)
      localStorage.setItem(
        "allowSelfAssignEvaluation",
        String(allowSelfAssignEvaluation)
      );
    if (profileLandmarks !== undefined)
      localStorage.setItem(
        "profileLandmarks",
        JSON.stringify(profileLandmarks || {})
      );

    setUser({
      userId,
      email,
      orgName,
      orgId: org?._id || "",
      role,
      name,
      profileImgUrl,
      botIcon: globalBotData?.botIcon,
      allowSelfAssignEvaluation,
      profileLandmarks: profileLandmarks || null,
    });
    setRole(role || "");
    setOrg(org || null);

    identifyUser({ _id: userId, email, role }, org || null);
    void fetchNotificationCount();
    void fetchBotDetails(role);
  };

  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setOrg(null);
    setRole("");
    setNotificationCount(0);
    setIsBotChatEnabled(false);
    void router.push("/signin?resetTheme=1");
  }, [router]);

  useEffect(() => {
    registerLogoutHandler(logout);

    return () => {
      logoutHandler = null;
    };
  }, [logout]);

  const updateOrg = (newOrg: Organization) => {
    setOrg(newOrg);
    localStorage.setItem("org", JSON.stringify(newOrg));
  };

  const mergeOrg = useCallback((fields: Partial<Organization>) => {
    setOrg((current) => {
      const merged = { ...current, ...fields } as Organization;
      localStorage.setItem("org", JSON.stringify(merged));
      return merged;
    });
  }, []);

  const getFeatureName = useCallback(
    (defaultName: string, featureKey: string) => {
      if (!org?.customFeatureNames) return defaultName;

      const names = org.customFeatureNames;
      const key = featureKey.toLowerCase();

      return (
        names[featureKey] ||
        names[key] ||
        names[key.replace(/s$/, "")] ||
        names[key + "s"] ||
        defaultName
      );
    },
    [org]
  );

  const getRoleDisplayName = useCallback(
    (role: string, options: { plural?: boolean } = {}) =>
      resolveRoleDisplayName(role, {
        ...options,
        roleDisplayNames: org?.roleDisplayNames,
        customRole: org?.customRole,
      }),
    [org?.roleDisplayNames, org?.customRole]
  );

  const LANDING_PAGE_ROUTES: Record<string, string> = {
    dashboard: "/dashboard",
    trainings: "/home",
    assignments: "/assignments",
    feeds: "/feeds",
    dictionary: "/dictionary",
    history: "/history",
  };

  const getDefaultLandingRoute = useCallback(
    (overrideRole?: string, orgData?: any): string => {
      // Use passed orgData first (avoids React state timing issues after login),
      // then fall back to store state, then localStorage as last resort
      let effectiveOrg = orgData ?? org;
      if (!effectiveOrg && typeof window !== "undefined") {
        try {
          const storedOrg = localStorage.getItem("org");
          if (storedOrg) {
            effectiveOrg = JSON.parse(storedOrg);
          }
        } catch (e) {
          console.error(e);
        }
      }
      const key = effectiveOrg?.defaultLandingPage;
      if (key && LANDING_PAGE_ROUTES[key]) {
        return LANDING_PAGE_ROUTES[key];
      }
      // Fall back for default of empty value
      return "/dashboard";
    },
    [org]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        org,
        role,
        isAuthenticated: Boolean(user),
        isLoading,
        notificationCount,
        setNotificationCount,
        fetchNotificationCount,
        isBotChatEnabled,
        fetchBotDetails,
        login,
        logout,
        getFeatureName,
        getRoleDisplayName,
        getDefaultLandingRoute,
        updateOrg,
        mergeOrg,
        refreshUser,
        updateSessionFromStorage: loadFromStorage,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthStore = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthStore must be used within an AuthStoreProvider");
  }
  return context;
};
